// Header.jsx - строгий официальный хедер с анимированным логотипом
import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef, useMemo } from 'react'
import AccessibilityPanel from '../AcessibilyPanel/AccessibiliyPanel'
import Link from '../Link/Link'
import style from './header.module.scss'
import logo from '../../assets/sambologo.jpg'
import { FaBars, FaTimes, FaSearch, FaPhone } from 'react-icons/fa'
import SearchBar from '../SearchBar/SearchBar'

const Header = () => {
    const navigate = useNavigate()
    const location = useLocation()
    
    const [isBlindMenuOpen, setIsBlindMenuOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isSearchVisible, setIsSearchVisible] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [activeTab, setActiveTab] = useState('')
    
    const navRef = useRef(null)
    const linkRefs = useRef([])

    const navItems = useMemo(() => [
        { id: 'about', label: 'Сведения об образовательной организации', path: '/about/' },
        { id: 'programs', label: 'Направления', path: '/programs/' },
        { id: 'units', label: 'Отделения', path: '/units/' },
        { id: 'team', label: 'Тренеры', path: '/team/' },
        { id: 'news', label: 'Новости', path: '/news/' },
        { id: 'contacts', label: 'Контакты', path: '/contacts/' },
    ], [])

    useEffect(() => {
        const currentPath = location.pathname
        const activeItem = navItems.find(item => currentPath.startsWith(item.path))
        if (activeItem) {
            setActiveTab(activeItem.id)
        }
    }, [location.pathname, navItems])

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.matchMedia('(max-width: 768px)').matches)
        }
        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    const toggleBlindMenu = () => {
        setIsBlindMenuOpen(prev => !prev)
        setIsMobileMenuOpen(false)
        setIsSearchVisible(false)
    }

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(prev => !prev)
        setIsSearchVisible(false)
        setIsBlindMenuOpen(false)
        document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : ''
    }

    const toggleSearch = () => {
        setIsSearchVisible(prev => !prev)
        setIsMobileMenuOpen(false)
        setIsBlindMenuOpen(false)
    }

    const handleTabClick = (tabId, path) => {
        setActiveTab(tabId)
        if (isMobile) {
            setIsMobileMenuOpen(false)
            document.body.style.overflow = ''
        }
        navigate(path)
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobileMenuOpen && !event.target.closest(`.${style.mobileHeader}`)) {
                setIsMobileMenuOpen(false)
                document.body.style.overflow = ''
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [isMobileMenuOpen])

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsMobileMenuOpen(false)
                setIsSearchVisible(false)
                setIsBlindMenuOpen(false)
                document.body.style.overflow = ''
            }
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [])

    const contacts = {
        phone: '+7 (861) 990-55-10'
    }

    // Мобильная версия
    if (isMobile) {
        return (
            <>
                <header className={style.mobileHeader}>
                    <div className={style.logoWrapper} onClick={() => navigate('/')}>
                        <div className={style.logoGlow}>
                            <img
                                src={logo}
                                className={style.logo}
                                alt='Логотип школы самбо'
                                loading="eager"
                                decoding="async"
                                draggable="false"
                            />
                        </div>
                    </div>
                    
                    <div className={style.mobileControls}>
                        <a href={`tel:${contacts.phone}`} className={style.phoneLink} aria-label="Позвонить">
                            <FaPhone />
                        </a>
                        <button 
                            className={style.searchToggle} 
                            onClick={toggleSearch}
                            aria-label="Поиск"
                        >
                            {isSearchVisible ? <FaTimes /> : <FaSearch />}
                        </button>
                        <AccessibilityPanel onClick={toggleBlindMenu} isOpen={isBlindMenuOpen} />
                        <button 
                            className={style.menuToggle} 
                            onClick={toggleMobileMenu}
                            aria-label={isMobileMenuOpen ? "Закрыть меню" : "Открыть меню"}
                        >
                            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    </div>

                    {isMobileMenuOpen && (
                        <nav className={style.mobileWrapperLinks}>
                            {navItems.map((item) => (
                                <Link 
                                    key={item.id}
                                    children={item.label}
                                    href={item.path}
                                    color={activeTab === item.id ? '#003366' : '#333'} 
                                    fontFamily='Roboto, sans-serif' 
                                    fontSize='1rem' 
                                    fontWeight={activeTab === item.id ? '600' : '400'}
                                    className={activeTab === item.id ? style.mobileActive : ''}
                                    onClick={() => handleTabClick(item.id, item.path)}
                                />
                            ))}
                            <div className={style.mobileContact}>
                                <a href={`tel:${contacts.phone}`}>
                                    <FaPhone />
                                    <span>{contacts.phone}</span>
                                </a>
                            </div>
                        </nav>
                    )}
                </header>
                
                {isSearchVisible && (
                    <div className={style.searchWrapper}>
                        <SearchBar 
                            isMobile={isMobile}
                            searchType="news"
                            onClose={() => setIsSearchVisible(false)}
                        />
                    </div>
                )}
            </>
        )
    }

    // Десктопная версия
    return (
        <>
            <header className={style.header}>
                <div className={style.wrapper}>
                    <div className={style.logoWrapper} onClick={() => navigate('/')}>
                        <div className={style.logoGlow}>
                            <img
                                src={logo}
                                className={style.logo}
                                alt='Логотип школы самбо'
                                loading="eager"
                                decoding="async"
                                draggable="false"
                            />
                        </div>
                        <div className={style.logoText}>
                            <span className={style.logoTitle}></span>
                            <span className={style.logoSubtitle}></span>
                        </div>
                    </div>
                    
                    <nav className={style.navLinks} ref={navRef}>
                        {navItems.map((item, index) => (
                            <Link 
                                key={item.id}
                                ref={(el) => (linkRefs.current[index] = el)}
                                children={item.label}
                                href={item.path}
                                color={activeTab === item.id ? '#003366' : '#333'}
                                fontFamily='Roboto, sans-serif'
                                fontSize='0.85rem'
                                fontWeight={activeTab === item.id ? '600' : '400'}
                                className={activeTab === item.id ? style.active : ''}
                                onClick={() => handleTabClick(item.id, item.path)}
                            />
                        ))}
                    </nav>
                    
                    <div className={style.utilityItems}>
                        <a href={`tel:${contacts.phone}`} className={style.phoneLink}>
                            <FaPhone />
                            <span>{contacts.phone}</span>
                        </a>
                        <button 
                            className={style.searchToggle} 
                            onClick={toggleSearch}
                            aria-label="Поиск"
                        >
                            {isSearchVisible ? <FaTimes /> : <FaSearch />}
                        </button>
                        <AccessibilityPanel onClick={toggleBlindMenu} isOpen={isBlindMenuOpen} />
                    </div>
                </div>
            </header>
            
            {isSearchVisible && (
                <div className={style.searchWrapper}>
                    <SearchBar 
                        isMobile={isMobile}
                        searchType="news"
                        onClose={() => setIsSearchVisible(false)}
                    />
                </div>
            )}
        </>
    )
}

export default Header